// scenario.ts — scenario editor form state (€ / % floats) and the API-boundary
// conversion functions that turn it into the frozen wire contract (integer cents/bp).
// This is the module the "API-boundary conversion" tests exercise.
import { mulDiv } from '@etf-cockpit/engine';
import type { ForecastConfig, SimulationResult } from '@etf-cockpit/engine';
import { centsToEur, eurToCents, monthIndexFromYearMonth, percentToBp } from './convert.js';
import { defaultMonteCarloSettings } from './montecarlo.js';
import type { MonteCarloPathCount } from './montecarlo.js';
import { defaultSettings } from './settings.js';
import type { AppSettings } from './settings.js';
import type { BacktestMeta, FundInfo } from './types.js';
import type { WaterfallMode } from './waterfall-geometry.js';

export type ScenarioMode = 'forecast' | 'backtest';

/** One row of the Sonderzahlungen (Einmalige Sonderzahlungen) editor. `id` is form-only
 * (list-rendering key / add-remove identity) and never crosses the API boundary — only
 * `yearMonth` (converted centrally via convert.ts's monthIndexFromYearMonth) and
 * `amountEur` (via eurToCents) do, in buildForecastConfig below. */
export interface ExtraContributionRow {
  id: string;
  /** "YYYY-MM" from an <input type="month">; '' until the user picks one (row not yet
   * usable — filtered out at the API boundary, see buildForecastConfig). */
  yearMonth: string;
  amountEur: number;
}

export interface ForecastFormState {
  /** null = "Eigene Parameter" (forecast only; backtest always needs a real fund). */
  fundIsin: string | null;
  startDate: string;
  annualReturnPercent: number;
  incomeYieldPercent: number;
  terPercent: number;
  kind: 'acc' | 'dist';
  meldefonds: boolean;
  monthlyEur: number;
  lumpSumEur: number;
  executionFeeEur: number;
  years: number;
  sellAtEnd: boolean;
  /** KESt rate override, in percent (27.5 = default). Converted to ForecastConfig.kestBp. */
  kestPercent: number;
  /** Only meaningful (and only shown in the UI) when kind === 'dist' — the engine only
   * reads it on that branch (forecast.ts: distFreq only affects fund.kind === 'dist'). */
  distributionFrequency: 'annual' | 'quarterly';
  /** Sparplan-Dynamik: annual automatic increase of the monthly rate, in percent
   * (0 = off, today's behaviour). Converted to ForecastConfig.contributionStepUpBp. */
  contributionStepUpPercent: number;
  /** Entnahmeplan (decumulation), forecast only, off by default. When true, the two
   * fields below are sent as ForecastConfig.withdrawal; when false, withdrawal is
   * omitted entirely (today's behaviour, byte-identical). */
  withdrawalEnabled: boolean;
  /** Target GROSS withdrawal per month, in €. Meaningless (and hidden in the UI) while
   * withdrawalEnabled is false. */
  withdrawalMonthlyGrossEur: number;
  /** Withdrawal phase duration in years (converted to months at the API boundary, same
   * rounding as the accumulation `years` field). */
  withdrawalYears: number;
  /** Einmalige Sonderzahlungen (one-off extra contributions), off by default (empty
   * array — additive, no-op unless the user adds a row). Converted to
   * ForecastConfig.extraContributions at the API boundary (see buildForecastConfig). */
  extraContributions: ExtraContributionRow[];
  /** Monte-Carlo / Renditeband settings, persisted per scenario (D-0xx-style additive
   * field — NOT the section's toggle-open state, which is local UI state; see
   * MonteCarloSection). Converted to volatilityBp at the API boundary via
   * buildMonteCarloRequestBody (lib/montecarlo.ts), same %->bp pattern as every other
   * rate field. */
  mcVolatilityPercent: number;
  mcPaths: MonteCarloPathCount;
  mcSeed: number;
}

export interface BacktestFormState {
  fundIsin: string | null;
  startDate: string;
  endDate: string;
  executionDayOfMonth: number;
  monthlyEur: number;
  lumpSumEur: number;
  executionFeeEur: number;
  sellAtEnd: boolean;
  /** Informational only (D-007) — omit the field entirely to omit the estimate. */
  terBpEstimatePercent: number | null;
  /** Sparplan-Dynamik: annual automatic increase of the monthly rate, in percent
   * (0 = off, today's behaviour). Converted to BacktestRequestBody.contributionStepUpBp. */
  contributionStepUpPercent: number;
}

export interface ScenarioState {
  mode: ScenarioMode;
  label: string;
  forecast: ForecastFormState;
  backtest: BacktestFormState;
}

/** The outcome of auto-computing one scenario. Shared between CockpitView (which shows
 * the active scenario's live result) and VergleichView (which reads the same results,
 * for every scenario at once, without re-running anything) — lifted here, rather than
 * owned by either view, so it isn't tied to one particular screen. */
export interface RunResult {
  status: 'idle' | 'loading' | 'error' | 'success';
  result?: SimulationResult;
  meta?: BacktestMeta;
  error?: string;
  mode?: WaterfallMode;
  /** The exact ForecastConfig that produced `result` — present only for mode==='forecast'.
   * Lets MonteCarloSection run the SAME config through /api/simulate/montecarlo instead
   * of re-deriving it from whatever the form currently shows (which may have been edited
   * since the last auto-compute, and would then disagree with the Verlauf chart). */
  config?: ForecastConfig;
  /** True while a RECOMPUTE is in flight for a scenario that already has a `status:
   * 'success'` result from a previous config (auto-compute: app.tsx's effect debounces
   * ~200ms then re-runs whenever a scenario's own inputs change). Deliberately separate
   * from `status` — `status` stays 'success' and `result`/`mode`/`config` keep pointing at
   * the LAST GOOD result the whole time `pending` is true, so the UI can show a small
   * "wird aktualisiert…" indicator without blanking or replacing what's on screen. The
   * FIRST-ever compute for a scenario (nothing previous to keep showing) still goes
   * through `status: 'loading'` instead — `pending` only applies to a recompute. */
  pending?: boolean;
}

export const IDLE_RUN_RESULT: RunResult = { status: 'idle' };

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function yearsAgoIso(years: number): string {
  const d = new Date();
  d.setUTCFullYear(d.getUTCFullYear() - years);
  return d.toISOString().slice(0, 10);
}

/** @param settings App-level defaults (Einstellungen); factory defaults when omitted
 * (e.g. in tests, or before the settings page has ever loaded). */
export function defaultForecastForm(settings: AppSettings = defaultSettings()): ForecastFormState {
  const mc = defaultMonteCarloSettings();
  return {
    fundIsin: null,
    startDate: todayIso(),
    annualReturnPercent: settings.annualReturnPercent,
    incomeYieldPercent: settings.incomeYieldPercent,
    terPercent: settings.terPercent,
    kind: 'acc',
    meldefonds: true,
    monthlyEur: settings.monthlyEur,
    lumpSumEur: settings.lumpSumEur,
    executionFeeEur: settings.executionFeeEur,
    years: 10,
    sellAtEnd: false,
    kestPercent: settings.kestPercent,
    distributionFrequency: settings.distributionFrequency,
    contributionStepUpPercent: settings.contributionStepUpPercent,
    // Entnahmeplan: off by default for every new/reset scenario (no Einstellungen
    // default needed per its own feature note — this is an opt-in, not a house default).
    // The two magnitude fields get sane placeholder values so the form isn't blank the
    // moment a user flips the toggle on.
    withdrawalEnabled: false,
    withdrawalMonthlyGrossEur: 1000,
    withdrawalYears: 20,
    extraContributions: [],
    mcVolatilityPercent: mc.volatilityPercent,
    mcPaths: mc.paths,
    mcSeed: mc.seed,
  };
}

export function defaultBacktestForm(settings: AppSettings = defaultSettings()): BacktestFormState {
  return {
    fundIsin: null,
    startDate: yearsAgoIso(5),
    endDate: todayIso(),
    executionDayOfMonth: 1,
    monthlyEur: settings.monthlyEur,
    lumpSumEur: settings.lumpSumEur,
    executionFeeEur: settings.executionFeeEur,
    sellAtEnd: false,
    terBpEstimatePercent: null,
    contributionStepUpPercent: settings.contributionStepUpPercent,
  };
}

export function createScenario(label: string, settings: AppSettings = defaultSettings()): ScenarioState {
  return {
    mode: 'forecast',
    label,
    forecast: defaultForecastForm(settings),
    backtest: defaultBacktestForm(settings),
  };
}

/** Prefills fund-derived fields when the user picks a fund in the forecast editor.
 * `annualReturnPercent` is prefilled from the fund's cached-history CAGR (bp -> %,
 * rounded to 1 decimal) when available — an honest "what happened since inception"
 * starting point, not a prediction (ForecastFields shows a hint saying exactly that);
 * left untouched when the fund has too little cached history to compute one.
 * "Eigene Parameter" (fund === null) never touches the user's existing values. */
export function applyFundToForecastForm(
  form: ForecastFormState,
  fund: FundInfo | null,
): ForecastFormState {
  if (!fund) {
    return { ...form, fundIsin: null };
  }
  return {
    ...form,
    fundIsin: fund.isin,
    kind: fund.kind,
    meldefonds: fund.meldefonds,
    terPercent: fund.terBp !== null ? fund.terBp / 100 : form.terPercent,
    annualReturnPercent:
      fund.cagrSinceInceptionBp !== null
        ? Math.round(fund.cagrSinceInceptionBp / 10) / 10
        : form.annualReturnPercent,
  };
}

/** True when the backtest form's date range is still exactly today's factory default
 * (defaultBacktestForm's own yearsAgoIso(5)/todayIso()) — i.e. the user hasn't edited
 * either date field yet. Used by applyFundToBacktestForm below to decide whether picking
 * a fund is allowed to set the range, vs. a user-chosen range that must never be
 * silently overwritten. */
export function isBacktestRangeAtDefault(form: BacktestFormState): boolean {
  return form.startDate === yearsAgoIso(5) && form.endDate === todayIso();
}

/** Backtest's fund-select prefill (D-0xx "default range = inception"): when a fund is
 * picked and the date range is still untouched (isBacktestRangeAtDefault), defaults the
 * range to that fund's own earliest/latest cached price (coverage.priceFrom/priceTo) —
 * so a fresh backtest spans the oldest-possible range instead of an arbitrary "last 5
 * years" window. Mirrors applyFundToForecastForm's shape/spirit (fund-derived prefill,
 * never touching a value the user has already set) but for the backtest form's dates
 * instead of the forecast form's return/TER fields. Never invents a bound the fund's own
 * coverage doesn't report: a null priceFrom/priceTo leaves that side of the range as-is.
 * "Eigene Parameter" doesn't exist in backtest mode (a real fund is always required), so
 * unlike applyFundToForecastForm this has no fund===null branch beyond clearing fundIsin. */
export function applyFundToBacktestForm(form: BacktestFormState, fund: FundInfo | null): BacktestFormState {
  if (!fund) return { ...form, fundIsin: null };
  const withFund: BacktestFormState = { ...form, fundIsin: fund.isin };
  if (!isBacktestRangeAtDefault(form)) return withFund;
  return {
    ...withFund,
    startDate: fund.coverage.priceFrom ?? withFund.startDate,
    endDate: fund.coverage.priceTo ?? withFund.endDate,
  };
}

/** Builds the engine's ForecastConfig verbatim (integer cents/bp) from € / % form state. */
export function buildForecastConfig(form: ForecastFormState): ForecastConfig {
  const months = Math.max(1, Math.round(form.years * 12));
  // Einmalige Sonderzahlungen: drop rows the user hasn't finished filling in (no month
  // picked yet, or a zero/negative amount) rather than sending a useless entry — matches
  // the engine's own "grossCents <= 0 is ignored" guard, just done client-side too so an
  // empty/half-filled row never even reaches the wire.
  const extraContributions = form.extraContributions
    .filter((row) => row.yearMonth !== '' && row.amountEur > 0)
    .map((row) => ({
      monthIndex: monthIndexFromYearMonth(form.startDate, row.yearMonth),
      grossCents: eurToCents(row.amountEur),
    }));
  return {
    startDate: form.startDate,
    months,
    lumpSumCents: eurToCents(form.lumpSumEur),
    monthlyCents: eurToCents(form.monthlyEur),
    executionFeeCents: eurToCents(form.executionFeeEur),
    annualReturnBp: percentToBp(form.annualReturnPercent),
    terBp: percentToBp(form.terPercent),
    fund: {
      kind: form.kind,
      meldefonds: form.meldefonds,
      incomeYieldBp: percentToBp(form.incomeYieldPercent),
      // Only meaningful for distributing funds (forecast.ts); omitted for 'acc' rather
      // than sent-but-ignored, so the wire payload doesn't imply it does anything there.
      ...(form.kind === 'dist' ? { distributionFrequency: form.distributionFrequency } : {}),
    },
    kestBp: percentToBp(form.kestPercent),
    sellAtEnd: form.sellAtEnd,
    // Omitted (rather than sent as an explicit 0) when there's no dynamic configured, so
    // the wire payload for the common case is byte-identical to before this feature.
    ...(form.contributionStepUpPercent > 0
      ? { contributionStepUpBp: percentToBp(form.contributionStepUpPercent) }
      : {}),
    // Entnahmeplan: omitted entirely when off (today's behaviour, byte-identical) rather
    // than sent-but-inert — the engine treats withdrawal as mutually exclusive with
    // sellAtEnd (withdrawal wins), so only emitting it when the user actually opted in
    // keeps `sellAtEnd` meaningful for scenarios that never touch this feature.
    ...(form.withdrawalEnabled
      ? {
          withdrawal: {
            monthlyGrossCents: eurToCents(form.withdrawalMonthlyGrossEur),
            months: Math.max(1, Math.round(form.withdrawalYears * 12)),
          },
        }
      : {}),
    // Omitted (rather than sent as an explicit []) when there are no usable rows, so the
    // wire payload for the common case is byte-identical to before this feature.
    ...(extraContributions.length > 0 ? { extraContributions } : {}),
  };
}

/**
 * Client-side preview of the monthly contribution AFTER every Sparplan-Dynamik step-up
 * over the full plan duration — computed the EXACT same way forecast.ts's monthly driver
 * does (same mulDiv, same "every 12th month" boundary), reusing the engine's own mulDiv
 * rather than reimplementing rounding. Returns monthlyEur unchanged (no fabricated
 * number) when there's no dynamic configured or no monthly contribution at all — matches
 * the engine's own no-op guard.
 */
export function finalMonthlyEurAfterStepUp(form: ForecastFormState): number {
  const stepUpBp = percentToBp(form.contributionStepUpPercent);
  const monthlyCents = eurToCents(form.monthlyEur);
  if (stepUpBp <= 0 || monthlyCents <= 0) return form.monthlyEur;

  const months = Math.max(1, Math.round(form.years * 12));
  // Mirrors forecast.ts's runForecastMonthly: month index i>0 && i%12===0 steps up the
  // rate; the number of such crossings within [0, months) is floor((months-1)/12).
  const numStepUps = Math.floor((months - 1) / 12);

  let currentCents = monthlyCents;
  for (let i = 0; i < numStepUps; i++) {
    currentCents = mulDiv(currentCents, 10000 + stepUpBp, 10000);
  }
  return centsToEur(currentCents);
}

/** Body for POST /api/simulate/backtest (NOT the engine's BacktestConfig — the server
 * resolves fund kind/meldefonds/prices from the ISIN + cache; see the frozen contract). */
export interface BacktestRequestBody {
  isin: string;
  startDate: string;
  endDate: string;
  executionDayOfMonth: number;
  lumpSumCents: number;
  monthlyCents: number;
  executionFeeCents: number;
  sellAtEnd: boolean;
  terBpEstimate?: number;
  contributionStepUpBp?: number;
}

export function buildBacktestRequestBody(
  form: BacktestFormState,
  isin: string,
): BacktestRequestBody {
  return {
    isin,
    startDate: form.startDate,
    endDate: form.endDate,
    executionDayOfMonth: form.executionDayOfMonth,
    lumpSumCents: eurToCents(form.lumpSumEur),
    monthlyCents: eurToCents(form.monthlyEur),
    executionFeeCents: eurToCents(form.executionFeeEur),
    sellAtEnd: form.sellAtEnd,
    ...(form.terBpEstimatePercent !== null
      ? { terBpEstimate: percentToBp(form.terBpEstimatePercent) }
      : {}),
    // Omitted (rather than sent as an explicit 0) when there's no dynamic configured, so
    // the wire payload for the common case is byte-identical to before this feature.
    ...(form.contributionStepUpPercent > 0
      ? { contributionStepUpBp: percentToBp(form.contributionStepUpPercent) }
      : {}),
  };
}
